export type ElasticnoteBootSurface = 'editor' | 'projects' | 'home' | 'saas';

export const ELASTICNOTE_LOCATION_CHANGE_EVENT = 'elasticnote:locationchange';

/**
 * Browser history mutation does not emit popstate. Elasticnote has two top-level
 * React applications (SaaS + editor), so history changes must be observable at
 * that boundary as well as by the inner routers. Install this once before
 * services are created so /projects -> /ws deep links cannot remain trapped in
 * the SaaS shell.
 */
export function installElasticnoteLocationChangeBridge(windowRef: Window): void {
  const history = windowRef.history as History & { __elasticnoteLocationBridge?: boolean };
  if (history.__elasticnoteLocationBridge) return;
  history.__elasticnoteLocationBridge = true;
  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);
  history.pushState = (data: unknown, unused: string, url?: string | URL | null) => {
    originalPushState(data, unused, url);
    windowRef.dispatchEvent(new Event(ELASTICNOTE_LOCATION_CHANGE_EVENT));
  };
  history.replaceState = (data: unknown, unused: string, url?: string | URL | null) => {
    originalReplaceState(data, unused, url);
    windowRef.dispatchEvent(new Event(ELASTICNOTE_LOCATION_CHANGE_EVENT));
  };
}

/**
 * Chooses the top-level Elasticnote application from the browser entry URL.
 * `/ws` is reserved for the local editor shell. Legacy `route=welcome`
 * entries now return to the public home page, while a truly bare `/ws`
 * remains the compatibility redirect to Projects. Other hashes are editor
 * deep links.
 */
export function resolveElasticnoteBootSurface(
  pathname: string,
  hash: string,
): ElasticnoteBootSurface {
  const isWsPath = pathname === '/ws' || pathname.startsWith('/ws/');
  if (!isWsPath) return 'saas';

  const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  if (hashParams.get('route') === 'welcome') return 'home';

  return hash ? 'editor' : 'projects';
}
